import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { Shell } from "./_shell";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const load = () => {
    setLoading(true);
    api.get("/orders").then(r => setOrders(r.data)).catch(() => setOrders([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (o, status) => {
    try {
      await api.put(`/orders/${o.id}/status`, { status });
      toast.success(`Order #${o.inquiry_no} → ${status}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const doDelete = async (o) => {
    if (!window.confirm(`Delete order #${o.inquiry_no} from ${o.business_name}? This cannot be undone.`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const filtered = filter === "all" ? orders : orders.filter(o => o.status === filter);
  const badgeClass = (s) => ({
    pending: "bg-safety text-ink", confirmed: "bg-cobalt text-paper",
    shipped: "bg-ink text-paper", delivered: "bg-green-700 text-paper",
    cancelled: "bg-soda text-paper",
  }[s] || "bg-black/10");
  const payLabel = (m) => ({
    cash: "Cash",
    electronic_or_cheque: "E-Transfer / Cheque",
    mixed: "Mixed",
    etransfer: "E-Transfer / Cheque",
    cheque: "E-Transfer / Cheque",
  }[m] || m);
  return (
    <Shell active="orders">
      <div className="p-5 md:p-10">
        <div className="overline mb-2">Retailer orders</div>
        <h1 className="font-display text-4xl md:text-6xl uppercase leading-none mb-6">Orders.</h1>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="orders-filters">
          {["all", "pending", "confirmed", "shipped", "delivered", "cancelled"].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`chip ${filter === f ? "bg-ink text-paper" : ""}`} data-testid={`orders-filter-${f}`}>
              {f} {f === "all" ? `(${orders.length})` : `(${orders.filter(o => o.status === f).length})`}
            </button>
          ))}
        </div>

        <div className="border border-ink overflow-x-auto" data-testid="orders-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-ink bg-ink text-paper">
                <th className="text-left p-3 overline">#</th>
                <th className="text-left p-3 overline">Retailer</th>
                <th className="text-left p-3 overline">Items</th>
                <th className="text-left p-3 overline">Subtotal</th>
                <th className="text-left p-3 overline">Payment</th>
                <th className="text-left p-3 overline">Delivery</th>
                <th className="text-left p-3 overline">Status</th>
                <th className="text-right p-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} className="border-b border-black/10 align-top" data-testid={`order-${o.id}`}>
                  <td className="p-3 font-bold">#{o.inquiry_no}</td>
                  <td className="p-3">
                    <div className="font-bold">{o.business_name}</div>
                    <div className="text-xs text-black/60">{o.email}</div>
                    {o.phone && <div className="text-xs text-black/50">{o.phone}</div>}
                    <div className="text-[10px] text-black/40 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  </td>
                  <td className="p-3 text-xs">
                    {o.lines?.length || 0} line{(o.lines?.length || 0) === 1 ? "" : "s"} · {o.lines?.reduce((n, l) => n + l.quantity, 0)} units
                  </td>
                  <td className="p-3 font-bold">
                    ${o.subtotal?.toFixed(2)}
                    {(o.cash_subtotal > 0 || o.electronic_subtotal > 0) && (
                      <div className="text-[10px] font-normal text-black/60 mt-1" data-testid={`order-split-${o.id}`}>
                        {o.cash_subtotal > 0 && <div>Cash: ${o.cash_subtotal.toFixed(2)}</div>}
                        {o.electronic_subtotal > 0 && <div>E-Trans/Cheque: ${o.electronic_subtotal.toFixed(2)}</div>}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">{payLabel(o.payment_method || "cash")}</span>
                  </td>
                  <td className="p-3 text-xs capitalize">{o.delivery_method}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${badgeClass(o.status)}`}>{o.status}</span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex flex-col gap-1 items-end">
                      <select value={o.status} onChange={e => setStatus(o, e.target.value)} className="field text-[11px] py-1 max-w-[130px]" data-testid={`order-status-${o.id}`}>
                        <option value="pending">pending</option>
                        <option value="confirmed">confirmed</option>
                        <option value="shipped">shipped</option>
                        <option value="delivered">delivered</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                      <Link to={`/order/${o.id}/print`} target="_blank" className="text-[11px] underline" data-testid={`order-print-${o.id}`}>Print form</Link>
                      <button onClick={() => doDelete(o)} className="text-[11px] underline text-soda" data-testid={`order-delete-${o.id}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan="8" className="p-8 text-center text-black/50">No orders in this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
